/**
 * ShowroomSelectionPopup Component
 *
 * A popup that appears when user hasn't selected a showroom yet.
 * Requests location permission and shows nearest showroom if available.
 *
 * Flow:
 * 1. Check if user has selected showroom (cookie) → If yes, don't show popup
 * 2. Check if popup was dismissed this session → If yes, don't show popup
 * 3. Request geolocation permission
 * 4. If granted, search for nearest showroom and display it
 * 5. User can confirm (set as My Showroom) or find another
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {useSearchStores} from '@salesforce/commerce-sdk-react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Skeleton,
  Spinner,
  Text,
  useBreakpointValue,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Custom close icon matching design specs
 * 16x16px, white stroke, opacity 0.7
 */
const PopupCloseIcon = (props) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <g opacity="0.7">
      <path d="M0.5 15.5L15.5 0.5" stroke="white" strokeLinecap="round" />
      <path d="M15.5 15.5L0.5 0.5" stroke="white" strokeLinecap="round" />
    </g>
  </Icon>
);

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

import {
  getSiteConfig,
  FALLBACK_STORE_SEARCH_RADIUS,
} from '@amblique/king-living/app/constants/site-config';
import {
  generateSlug,
  getMyShowroomFromCookie,
  isShowroomPopupDismissed,
  setMyShowroomToCookie,
  setShowroomPopupDismissed,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';

// Location states
const LOCATION_STATE = {
  IDLE: 'idle',
  REQUESTING: 'requesting',
  GRANTED: 'granted',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
};

/**
 * ShowroomSelectionPopup - Detects location and shows nearest showroom
 */
const ShowroomSelectionPopup = () => {
  const intl = useIntl();
  const navigate = useNavigation();
  const {site, buildUrl} = useMultiSite();

  const siteConfig = getSiteConfig(site?.id);

  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [locationState, setLocationState] = useState(LOCATION_STATE.IDLE);
  const [userCoords, setUserCoords] = useState(null);
  const [detectedShowroom, setDetectedShowroom] = useState(null);
  const [searchCompleted, setSearchCompleted] = useState(false);

  // Dynamic position state for popup
  const [popupPosition, setPopupPosition] = useState({top: 70, right: 270, arrowRight: 60});
  const popupRef = useRef(null);
  const isDesktop = useBreakpointValue({base: false, lg: true});

  // Popup width constant
  const POPUP_WIDTH = 400;

  // Search for nearest stores when we have coordinates
  // Use FALLBACK_STORE_SEARCH_RADIUS (10,000km) to ensure we find stores
  const {
    data: storesData,
    isLoading: isSearching,
    isError: isSearchError,
  } = useSearchStores(
    {
      parameters: {
        latitude: userCoords?.latitude,
        longitude: userCoords?.longitude,
        maxDistance: FALLBACK_STORE_SEARCH_RADIUS,
        distanceUnit: siteConfig?.distanceUnit || 'km',
        limit: 1,
        locale: siteConfig?.defaultLocale || 'en-AU',
      },
    },
    {
      enabled: !!userCoords && locationState === LOCATION_STATE.GRANTED && !searchCompleted,
    }
  );

  // Update detected showroom when store data arrives
  useEffect(() => {
    if (isSearchError) {
      setSearchCompleted(true);

      return;
    }

    if (storesData?.data?.[0]) {
      const store = storesData.data[0];

      setDetectedShowroom({
        id: store.id,
        name: store.name,
        slug: store.c_urlSlug || generateSlug(store.name) || store.id,
        city: store.city,
        stateCode: store.stateCode,
        postalCode: store.postalCode,
        distance: store.distance,
        lat: store.latitude,
        lng: store.longitude,
      });
      setSearchCompleted(true);
    } else if (storesData && !isSearching) {
      // Search completed but no stores found
      setSearchCompleted(true);
    }
  }, [storesData, isSearching, isSearchError]);

  // Request geolocation
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationState(LOCATION_STATE.UNAVAILABLE);

      return;
    }

    setLocationState(LOCATION_STATE.REQUESTING);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationState(LOCATION_STATE.GRANTED);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationState(LOCATION_STATE.DENIED);
        } else {
          setLocationState(LOCATION_STATE.UNAVAILABLE);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, []);

  // Check if popup should be shown
  useEffect(() => {
    setIsMounted(true);

    if (typeof window === 'undefined' || !site?.id) return;

    const hasShowroom = getMyShowroomFromCookie(site.id);
    const isDismissed = isShowroomPopupDismissed(site.id);

    if (!hasShowroom && !isDismissed) {
      // Show popup after delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        // Auto-request location when popup shows
        requestLocation();
      }, 1000);

      return () => clearTimeout(timer);
    }

    // Listen for showroom changes
    const handleShowroomChanged = (event) => {
      if (event.detail?.showroom) {
        setIsVisible(false);
      }
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [site?.id, requestLocation]);

  // Handle dismiss
  const handleDismiss = () => {
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
  };

  // Handle confirm detected showroom
  const handleConfirmShowroom = () => {
    if (detectedShowroom) {
      setMyShowroomToCookie(site?.id, detectedShowroom);
    }
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
  };

  // Handle find different showroom
  const handleFindShowroom = () => {
    setShowroomPopupDismissed(site?.id);
    setIsVisible(false);
    navigate(buildUrl('/showrooms'));
  };

  // Calculate popup position based on showroom button location
  const updatePopupPosition = useCallback(() => {
    if (!isDesktop || typeof window === 'undefined') return;

    // Find the showroom button in header by aria-label
    const showroomButton = document.querySelector('[aria-label*="showroom" i]') ||
      document.querySelector('[aria-label*="Showroom" i]');

    if (showroomButton) {
      const rect = showroomButton.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Try to find the text element within the button for more accurate centering
      const textElement = showroomButton.querySelector('span');
      let targetCenterFromRight;

      if (textElement) {
        // Use text element center for arrow positioning
        const textRect = textElement.getBoundingClientRect();

        targetCenterFromRight = viewportWidth - (textRect.left + textRect.width / 2);
      } else {
        // Fallback to button center
        targetCenterFromRight = viewportWidth - (rect.left + rect.width / 2);
      }

      // Position popup so arrow points to text center
      // Arrow is positioned from right edge of popup
      const arrowOffsetFromPopupRight = POPUP_WIDTH / 2; // Center arrow on popup
      const popupRight = targetCenterFromRight - arrowOffsetFromPopupRight;

      // Ensure popup doesn't go off-screen
      const minRight = 20;
      const maxRight = viewportWidth - POPUP_WIDTH - 20;
      const constrainedRight = Math.max(minRight, Math.min(popupRight, maxRight));

      // Recalculate arrow position if popup was constrained
      const arrowRight = targetCenterFromRight - constrainedRight;

      setPopupPosition({
        top: rect.bottom + 8, // 8px gap below button
        right: constrainedRight,
        arrowRight: Math.max(20, Math.min(arrowRight, POPUP_WIDTH - 20)), // Keep arrow within popup
      });
    }
  }, [isDesktop]);

  // Update position when popup becomes visible or window resizes
  useEffect(() => {
    if (!isVisible || !isDesktop) return;

    // Initial position calculation
    updatePopupPosition();

    // Update on resize
    window.addEventListener('resize', updatePopupPosition);

    // Update on scroll (in case of sticky header changes)
    window.addEventListener('scroll', updatePopupPosition, {passive: true});

    // Use MutationObserver to detect DOM changes (like promo banner closing)
    const observer = new MutationObserver(() => {
      // Small delay to let DOM settle
      setTimeout(updatePopupPosition, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      window.removeEventListener('resize', updatePopupPosition);
      window.removeEventListener('scroll', updatePopupPosition);
      observer.disconnect();
    };
  }, [isVisible, isDesktop, updatePopupPosition]);

  // Don't render on server or if not visible
  if (!isMounted || !isVisible) {
    return null;
  }

  // Determine what to show
  const isLoading = locationState === LOCATION_STATE.REQUESTING ||
    (locationState === LOCATION_STATE.GRANTED && isSearching && !searchCompleted);
  const hasDetectedShowroom = detectedShowroom && locationState === LOCATION_STATE.GRANTED;

  return (
    <Box
      ref={popupRef}
      bottom={{base: 'auto', lg: 'auto'}}
      left={{base: 4, lg: 'auto'}}
      position="fixed"
      right={{base: 4, lg: `${popupPosition.right}px`}}
      top={{base: '90px', lg: `${popupPosition.top}px`}}
      zIndex="popover"
    >
      {/* Arrow pointing up - dynamically positioned to align with showroom button */}
      <Box
        borderBottom="6px solid"
        borderBottomColor="black"
        borderLeft="6px solid transparent"
        borderRight="6px solid transparent"
        height="0"
        position="absolute"
        right={{base: '149px', lg: `${popupPosition.arrowRight}px`}}
        top="-6px"
        width="0"
      />

      <Box
        bg="black"
        borderRadius={{base: '8px', lg: '8px'}}
        boxShadow="xl"
        maxWidth={{base: '100%', lg: '400px'}}
        overflow="hidden"
        p={6}
        position="relative"
        width={{base: '100%', lg: '400px'}}
      >
        {/* Close button */}
        <IconButton
          _hover={{opacity: 1}}
          h="auto"
          icon={<PopupCloseIcon boxSize="16px" />}
          minW="auto"
          position="absolute"
          right={1.5}
          top={0}
          variant="unstyled"
          aria-label={intl.formatMessage({
            defaultMessage: 'Close',
            id: 'showroom_popup.close',
          })}
          onClick={handleDismiss}
        />

        {/* Loading state */}
        {isLoading && (
          <VStack align="stretch" spacing={4}>
            <Heading as="h3" color="white" fontWeight="bold" pr={8} size="md">
              <FormattedMessage
                defaultMessage="Tailor your KING experience"
                id="showroom_popup.title"
              />
            </Heading>
            <Flex align="center" gap={2}>
              <Spinner color="white" size="sm" />
              <Text color="gray.300" fontSize="sm">
                <FormattedMessage
                  defaultMessage="Finding your nearest showroom..."
                  id="showroom_popup.loading"
                />
              </Text>
            </Flex>
            <Skeleton endColor="gray.600" height="48px" startColor="gray.700" />
          </VStack>
        )}

        {/* Detected showroom */}
        {!isLoading && hasDetectedShowroom && (
          <VStack align="stretch" spacing={5}>
            <Box pr={8}>
              <Heading as="h3" color="white" fontWeight="bold" mb={2} size="md">
                <FormattedMessage
                  defaultMessage="Tailor your KING experience"
                  id="showroom_popup.title"
                />
              </Heading>
              <Text
                _hover={{opacity: 0.8}}
                as="a"
                color="white"
                display="block"
                fontSize="sm"
                href={buildUrl(`/showrooms/${detectedShowroom.slug}`)}
                mb={2}
                textDecoration="underline"
                onClick={(e) => {
                  e.preventDefault();
                  handleDismiss();
                  navigate(buildUrl(`/showrooms/${detectedShowroom.slug}`));
                }}
              >
                {detectedShowroom.name}
                {(detectedShowroom.stateCode || detectedShowroom.postalCode) && (
                  <>, {[detectedShowroom.stateCode, detectedShowroom.postalCode].filter(Boolean).join(' ')}</>
                )}
              </Text>
              <Text color="white" fontSize="sm">
                <FormattedMessage
                  defaultMessage="We've found your nearest showroom for a more personalised experience."
                  id="showroom_popup.detected_description"
                />
              </Text>
            </Box>

            <HStack spacing={3}>
              <Button
                _hover={{bg: 'whiteAlpha.200'}}
                borderColor="white"
                color="white"
                flex={1}
                height="48px"
                variant="outline"
                onClick={handleConfirmShowroom}
              >
                <FormattedMessage
                  defaultMessage="Confirm store"
                  id="showroom_popup.confirm"
                />
              </Button>

              <Button
                _hover={{bg: 'gray.100'}}
                bg="white"
                color="black"
                flex={1}
                height="48px"
                onClick={handleFindShowroom}
              >
                <FormattedMessage
                  defaultMessage="Change store"
                  id="showroom_popup.change"
                />
              </Button>
            </HStack>
          </VStack>
        )}

        {/* No showroom found or location failed */}
        {!isLoading && !hasDetectedShowroom && (
          <VStack align="stretch" spacing={5}>
            <Box pr={8}>
              <Heading as="h3" color="white" fontWeight="bold" mb={2} size="md">
                <FormattedMessage
                  defaultMessage="Tailor your KING experience"
                  id="showroom_popup.title"
                />
              </Heading>
              <Text color="white" fontSize="sm">
                <FormattedMessage
                  defaultMessage="Set your local KING showroom for a more personalised experience."
                  id="showroom_popup.description"
                />
              </Text>
            </Box>

            <Button
              _hover={{bg: 'gray.100'}}
              bg="white"
              color="black"
              height="48px"
              width="100%"
              onClick={handleFindShowroom}
            >
              <FormattedMessage
                defaultMessage="Choose showroom"
                id="showroom_popup.cta"
              />
            </Button>
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default ShowroomSelectionPopup;
